import { useEffect, useState } from "react";

import { add, format, isSameMonth, sub } from "date-fns";

import ChevronLeft from "@/assets/icons/chevron-left.svg";
import ChevronRight from "@/assets/icons/chevron-right.svg";

import { useCalendar } from "../CalendarContext";
import { useDayLabels } from "../hooks/useDayLabels";
import { usePaddedMonth } from "../hooks/usePaddedMonth";
import Month from "../Month";
import { CalendarComponentProps } from "../types";

import styles from "./CalendarBase.module.css";

export type CalendarBaseProps = CalendarComponentProps & {
  label?: string;
};

const CalendarBase: React.FC<CalendarBaseProps> = ({
  focused: _focused,
  value,
  label,
  onChange,
}) => {
  const [focused, setFocus] = useState<Date>(value || _focused || new Date());
  const settings = useCalendar();
  const labels = useDayLabels(settings);

  useEffect(() => {
    _focused && setFocus(_focused);
  }, [_focused]);

  useEffect(() => {
    value && setFocus(value);
  }, [value]);

  const days = usePaddedMonth(focused, settings);
  const nextDisabled = isSameMonth(focused, settings.max);
  const previousDisabled = isSameMonth(focused, settings.min);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.monthAndYear}>
          {format(focused, "MMMM yyyy", settings)}
        </div>
        <span className={styles.pushRight} />
        <button
          type="button"
          className={styles.action}
          disabled={previousDisabled}
          onClick={() => setFocus((d) => sub(d, { months: 1 }))}
        >
          <ChevronLeft />
        </button>
        <button
          type="button"
          className={styles.action}
          disabled={nextDisabled}
          onClick={() => setFocus((d) => add(d, { months: 1 }))}
        >
          <ChevronRight />
        </button>
      </header>
      <Month
        days={days}
        labels={labels}
        onChange={onChange}
        value={value}
        focused={focused}
      />
      <footer className={styles.footer}>
        {label && <span>{label}</span>}
        <time>{value ? format(value, "PPP", settings) : "-"}</time>
      </footer>
    </div>
  );
};

export default CalendarBase;
